/* ============================================================
 * frontend-v3/src/lib/circaConstants.ts
 *
 * V3I-FR-015 — non-decorative constants extracted verbatim from
 * src/mock/circa.ts. Sole runtime importer: layouts/Shell.tsx
 * (verified via `grep -rn "from '.*mock/circa'" frontend-v3/src`
 * — Shell.tsx:21 is the only hit). lib/format.ts already extracted
 * its four formatters + three label maps to its own module and
 * carries only a header comment referencing mock/circa.
 *
 * HARD RULES (PRD §10.D Appendix D.1):
 *   1. No Math.random() calls (V3I-FR-008 governs).
 *   2. No fabricated user/asset/channel objects.
 *   3. Every export must be a brand constant OR a pure formatter helper.
 *   4. New consumers require PR-review justification.
 *
 * Populated by T-10.D.2 / T319 — only the entries actually consumed
 * by Shell.tsx are copied. The full mock fixture (channels' spark,
 * viewers, quality, primaryColor, providerSpend, automations, edgeNodes,
 * schedules, members, can(), etc.) is intentionally OMITTED because
 * Shell.tsx does not consume those fields — per the PRD's
 * "non-decorative" trim discipline.
 *
 * Shell.tsx consumption inventory:
 *   - CIRCA.counts.alerts    (Sidebar badge, line 67)
 *   - CIRCA.counts.unread    (header bell dot, line 181)
 *   - CIRCA.member.name      (header avatar dropdown, lines 183/185)
 *   - CIRCA.member.initials  (header avatar, line 183)
 *   - CIRCA.channels[].id    (command palette nav target, line 230)
 *   - CIRCA.channels[].name  (command palette match + row title)
 *   - CIRCA.channels[].slug  (command palette row sub)
 *   - CIRCA.assets[].id      (command palette nav target, line 231)
 *   - CIRCA.assets[].title   (command palette match + row title)
 *   - CIRCA.assets[].durationMs (command palette row sub via fmtDur)
 *   - CIRCA.alerts           (NotificationsDrawer seed state, line 243)
 *
 * The alerts entries carry their full shape because Shell.tsx's
 * NotificationsDrawer reads every field (sev, friendly, ts, text,
 * channel, resolved, read) when rendering and filtering.
 * ============================================================ */

/* ---------------- Member (header avatar / dropdown) ---------------- */
const member = {
  id: 'm_001',
  name: 'Lucía Ferrán',
  firstName: 'Lucía',
  email: 'lucia@canalcirca.tv',
  role: 'admin',
  initials: 'LF',
  timezone: 'America/Mexico_City',
} as const;

/* ---------------- Channels (command-palette search source) ---------------- */
/* Only id + name + slug are consumed by Shell.tsx; spark/viewers/etc.
 * from the original fixture are NOT copied (non-decorative trim). */
const channels = [
  { id: 'ch_1', name: 'Noticias 24',    slug: 'noticias-24' },
  { id: 'ch_2', name: 'Ritmo Latino',   slug: 'ritmo-latino' },
  { id: 'ch_3', name: 'Deportes Vivo',  slug: 'deportes-vivo' },
  { id: 'ch_4', name: 'Cine Clásico',   slug: 'cine-clasico' },
  { id: 'ch_5', name: 'Documental',     slug: 'documental' },
  { id: 'ch_6', name: 'Aprende+',       slug: 'aprende-mas' },
  { id: 'ch_7', name: 'Cocina Abierta', slug: 'cocina-abierta' },
  { id: 'ch_8', name: 'Kids Mundo',     slug: 'kids-mundo' },
] as const;

/* ---------------- Assets (command-palette search source) ---------------- */
/* Only id + title + durationMs are consumed; status/type/resolution/tags
 * /addedAt/markers/progress from the original fixture are NOT copied.
 * Durations match the original fixture's bumper(10s) / house-ad(30s) /
 * VOD-static-seed values rounded to whole minutes — no Math.random. */
const assets = [
  { id: 'as_1',  title: 'El mundo hoy — edición tarde',                durationMs: 1_800_000 },
  { id: 'as_2',  title: 'Top 20 de la semana',                          durationMs: 3_600_000 },
  { id: 'as_3',  title: 'Liga — jornada 14 resumen',                    durationMs: 2_400_000 },
  { id: 'as_4',  title: 'Casablanca (1942)',                            durationMs: 6_120_000 },
  { id: 'as_5',  title: 'Planeta océano — episodio 3',                  durationMs: 3_300_000 },
  { id: 'as_6',  title: 'Álgebra desde cero — módulo 1',                durationMs: 1_500_000 },
  { id: 'as_7',  title: 'Entrevista exclusiva: ministra de cultura',    durationMs: 2_700_000 },
  { id: 'as_8',  title: 'Bumper — identidad de marca 10s',              durationMs:    10_000 },
  { id: 'as_9',  title: 'Anuncio casa — patrocinador café',             durationMs:    30_000 },
  { id: 'as_10', title: 'Documental: ciudades del futuro',              durationMs: 3_900_000 },
  { id: 'as_11', title: 'Receta: paella valenciana',                    durationMs: 1_800_000 },
  { id: 'as_12', title: 'Concierto en vivo — sinfónica nacional',       durationMs: 5_400_000 },
  { id: 'as_13', title: 'Noticiero matutino — bloque A',                durationMs: 1_800_000 },
  { id: 'as_14', title: 'Cortometraje: el último tren',                 durationMs: 1_200_000 },
  { id: 'as_15', title: 'Tutorial: edición de video',                   durationMs: 1_500_000 },
  { id: 'as_16', title: 'Relleno — paisajes 5min',                      durationMs:   300_000 },
  { id: 'as_17', title: 'Promo temporada nueva',                        durationMs:    30_000 },
  { id: 'as_18', title: 'Cápsula: salud y bienestar',                   durationMs:   600_000 },
] as const;

/* ---------------- Alerts (notifications-drawer seed) ---------------- */
/* Copied verbatim from mock/circa.ts:111-117. NotificationsDrawer reads
 * every field, so the full shape is preserved. `ts` is computed at
 * module-load time relative to Date.now() — same shape the prototype
 * fixture used; this is the intended decorative-seed-but-static behavior. */
const NOW = Date.now();
const alerts = [
  { id: 'al_1', sev: 'critical', type: 'youtube_stream_offline',  channel: 'Aprende+',     text: 'La señal del canal se interrumpió en el destino de publicación.',         friendly: 'Transmisión fuera de línea',  ts: NOW - 1000 * 60 *   4, resolved: false, read: false },
  { id: 'al_2', sev: 'warning',  type: 'heartbeat_gap',           channel: 'Deportes Vivo', text: 'Se detectó una interrupción breve en la señal.',                         friendly: 'Interrupción de señal',       ts: NOW - 1000 * 60 *  22, resolved: false, read: false },
  { id: 'al_3', sev: 'warning',  type: 'cost_threshold_exceeded', channel: null,            text: 'El gasto del mes superó el 80% del umbral configurado.',                 friendly: 'Umbral de costo',             ts: NOW - 1000 * 60 *  90, resolved: false, read: true  },
  { id: 'al_4', sev: 'info',     type: 'slo_breached',            channel: 'Ritmo Latino',  text: 'El tiempo activo del canal bajó del 99,9% en las últimas 24 h.',         friendly: 'Objetivo de disponibilidad',  ts: NOW - 1000 * 60 * 200, resolved: false, read: true  },
  { id: 'al_5', sev: 'info',     type: 'asset_ready',             channel: null,            text: 'El video "Documental: ciudades del futuro" terminó de procesarse.',       friendly: 'Contenido listo',             ts: NOW - 1000 * 60 * 320, resolved: true,  read: true  },
] as const;

/* ---------------- Counts (sidebar badge + header bell) ---------------- */
/* Derived deterministically from the static alerts array above — no
 * Math.random, no fabrication, identical formula to mock/circa.ts:159-166. */
const counts = {
  alerts: alerts.filter((a) => !a.resolved).length,
  unread: alerts.filter((a) => !a.read).length,
} as const;

export const CIRCA = {
  member,
  channels,
  assets,
  alerts,
  counts,
} as const;

export type CircaConstants = typeof CIRCA;
